import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { IReportsRepository } from "../../../domain/repositories/reports-repository";
import { TViewPortRanking } from "domain/entities/view-port-ranking-response";
import { ReportsFilterContext } from "./ReportsFilterProvider";
import { createBlobDownloader } from "utils/files/downloadBlob";
import { checkParamsFromCreative } from "helper/filtersHelper";

type TViewPortRankingProviderProps = {
  requestParamsIndex: number;
  children: React.ReactNode;
  reportsDatasource: IReportsRepository;
  showCards?: boolean;
};

export type TViewPortRankingSummaryState = {
  requestParamsIndex: number;
  viewPortRankingData?: TViewPortRanking;
  downloadingReports?: boolean;
  downloadViewportRankingReport: () => void;
  isLoading: boolean;
};

const initContext: TViewPortRankingSummaryState = {
  requestParamsIndex: 0,
  downloadViewportRankingReport: () => null,
  isLoading: true,
};
export const ViewPortRankingContext = createContext(initContext);

export const ViewPortRankingProvider = ({
  requestParamsIndex,
  children,
  reportsDatasource,
  showCards = true,
}: TViewPortRankingProviderProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [viewPortRankingData, setViewPortRankingData] =
    useState<TViewPortRanking>();
  const [downloadingReports, setDownloadingReports] = useState(false);
  const { requestParams, comparisonEnabled } = useContext(ReportsFilterContext);
  const currentRequestParams = requestParams[requestParamsIndex];

  const downloadViewportRankingReport = useCallback(() => {
    setDownloadingReports(true);
    return reportsDatasource
      .exportViewportRankingCSV(currentRequestParams)
      .then(createBlobDownloader("device-summary.zip"))
      .finally(() => setDownloadingReports(false));
  }, [currentRequestParams]);

  const canShowPanel = useMemo(() => {
    if (comparisonEnabled) {
      return requestParams.every(checkParamsFromCreative);
    }

    return checkParamsFromCreative(currentRequestParams);
  }, [requestParams]);

  useEffect(() => {
    if (canShowPanel) {
      setIsLoading(true);
      reportsDatasource
        .getViewPortRanking({ ...currentRequestParams, showCards })
        .then(setViewPortRankingData)
        .then(() => setIsLoading(false));
    }
  }, [currentRequestParams]);

  return (
    <ViewPortRankingContext.Provider
      value={{
        requestParamsIndex,
        downloadingReports,
        viewPortRankingData,
        downloadViewportRankingReport,
        isLoading,
      }}
    >
      {canShowPanel ? children : <></>}
    </ViewPortRankingContext.Provider>
  );
};
